/**
 * Mail order shipping & tax estimates (same rules as checkout cart).
 */

import { calculateShipping, calculateCartWeight } from './shippingConfig';
import { calculateTax } from './taxConfig';
import {
  normalizeCanadianProvince,
  resolveCountryCode,
  getStateProvinceValue,
} from './addressUtils';

/** Cart-shaped lines for shipping/tax utilities. */
export function buildMailOrderCartItems(computedLines) {
  return (computedLines || [])
    .filter((line) => line.product && line.productId)
    .map((line) => ({
      ...line.product,
      quantity: Number(line.quantity) || 1,
      unitPrice: line.unitPrice,
      price_cents: line.unitPrice,
      price: line.unitPrice,
    }));
}

export function resolveMailOrderDestination({ countryCode, country, province, state, region }) {
  const code = resolveCountryCode({
    countryCode,
    country,
    province,
    state,
    region,
  });
  const stateProvince = getStateProvinceValue(
    {
      countryCode: code,
      province: code === 'CA' ? normalizeCanadianProvince(province) : province,
      state,
      region,
    },
    code
  );
  return { countryCode: code, stateProvince };
}

/**
 * @returns {{ shippingCents, taxCents, shippingNote, taxNote, shippingInfo, taxInfo }}
 */
export async function calculateMailOrderTotals({
  computedLines,
  subtotalCents,
  countryCode = 'CA',
  province = '',
  state = '',
  region = '',
  taxSettings = [],
  shippingMethod = 'standard',
  shippingCentsOverride = null,
}) {
  const cartItems = buildMailOrderCartItems(computedLines);
  const { countryCode: country, stateProvince } = resolveMailOrderDestination({
    countryCode,
    province,
    state,
    region,
  });

  if (!cartItems.length || subtotalCents <= 0) {
    return {
      shippingCents: 0,
      taxCents: 0,
      shippingNote: 'Add products to calculate shipping',
      taxNote: 'Add products to calculate tax',
      shippingInfo: null,
      taxInfo: null,
    };
  }

  let shippingInfo = null;
  let shippingCents = 0;
  let shippingNote = '';

  if (shippingCentsOverride != null) {
    shippingCents = Math.max(0, Number(shippingCentsOverride) || 0);
    shippingNote = 'Manual shipping amount';
  } else {
    const totalWeightKg = calculateCartWeight(cartItems);
    shippingInfo = await calculateShipping(
      subtotalCents,
      totalWeightKg,
      country,
      shippingMethod,
      cartItems
    );
    shippingCents = shippingInfo?.cost || 0;
    shippingNote = shippingInfo?.isFree
      ? (shippingInfo.message || 'Free shipping')
      : (shippingInfo?.shippingBandLabel || shippingInfo?.message || 'Standard shipping estimate');
  }

  const taxInfo = calculateTax(
    subtotalCents,
    country,
    stateProvince || null,
    cartItems,
    taxSettings,
    shippingCents
  );
  const taxCents = taxInfo?.tax || 0;

  const taxNote = taxInfo?.breakdown?.length
    ? taxInfo.breakdown.map((row) => row.description).join(' · ')
    : (taxCents === 0 ? 'No tax for this destination' : 'Tax calculated');

  return {
    shippingCents,
    taxCents,
    shippingNote,
    taxNote,
    shippingInfo,
    taxInfo,
  };
}
